/**
 * SlideCountPicker — compact pill selector for preferred slide count.
 * Used on Step 1 (StepInput) and Step 2 (StepInsight).
 */

import React from "react";

// Most-used LinkedIn carousel sizes (1 = single image post)
const SLIDE_PILLS = [1, 2, 5, 8, 10] as const;

interface Props {
  value:    number | null;
  onChange: (v: number | null) => void;
}

const SlideCountPicker: React.FC<Props> = ({ value, onChange }) => {
  const handleCustom = (raw: string) => {
    const n = parseInt(raw, 10);
    onChange(Number.isFinite(n) && n >= 1 ? n : null);
  };

  return (
    <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
      <span style={{
        fontSize:      "12px",
        fontWeight:    600,
        color:         "#aaa",
        flexShrink:    0,
        letterSpacing: "0.02em",
      }}>
        Slides (optional)
      </span>

      {/* "Any" pill — clears preference */}
      <button style={pill(value === null)} onClick={() => onChange(null)}>
        Any
      </button>

      {/* Quick-pick pills */}
      {SLIDE_PILLS.map(n => (
        <button
          key={n}
          style={pill(value === n)}
          onClick={() => onChange(value === n ? null : n)}
        >
          {n}
        </button>
      ))}

      {/* Free-entry for anything not in the pills */}
      <input
        type="number"
        min={1}
        max={20}
        placeholder="other"
        value={
          value !== null && !(SLIDE_PILLS as readonly number[]).includes(value)
            ? value
            : ""
        }
        onChange={e => handleCustom(e.target.value)}
        style={{
          width:        "60px",
          padding:      "5px 8px",
          fontSize:     "12px",
          border:       "1.5px solid #E4E4E0",
          borderRadius: "100px",
          outline:      "none",
          textAlign:    "center",
          color:        "#444",
          background:   "#fff",
        }}
      />
    </div>
  );
};

const pill = (active: boolean): React.CSSProperties => ({
  padding:      "5px 13px",
  borderRadius: "100px",
  fontSize:     "12px",
  fontWeight:   600,
  cursor:       "pointer",
  border:       active ? "1.5px solid #5B4FE8" : "1.5px solid #E4E4E0",
  background:   active ? "rgba(91,79,232,0.08)" : "#fff",
  color:        active ? "#5B4FE8" : "#888",
  transition:   "all 0.15s",
});

export default SlideCountPicker;
